/**
 * User-facing copy for every voice failure the loop can hit. Codes are set by
 * useVoiceMode.js (capture + transcribe phases); the mapping lives here so the
 * dock, the hook and the tests share one source of truth. Unmapped/unknown
 * codes fall back to a generic line — a voice error is never silent.
 */
export const VOICE_ERROR_COPY = {
  mic_unsupported: 'Voice needs HTTPS and microphone access in this browser.',
  mic_denied: 'Microphone blocked — allow mic access for this site, then try again.',
  mic_missing: 'No microphone found on this device.',
  mic_failed: 'Could not start the microphone — try again.',
  stt_not_configured: 'Speech-to-text is not configured — add an STT key in Voice Settings.',
  rate_limited: 'Too many voice requests — wait a minute, then tap voice mode again.',
  voice_denied: 'Voice access is denied for your role.',
  transcribe_failed: 'Transcription failed — tap voice mode again and speak.',
  send_failed: 'Could not send that message — try again.',
  // Correction is best-effort (the raw transcript is still sent) — never surfaced.
  correct_failed: null,
};

/** @param {string|null} code @returns {string} human copy for a voice error code. */
export function voiceErrorMessage(code) {
  if (!code) return '';
  if (code in VOICE_ERROR_COPY) return VOICE_ERROR_COPY[code] || '';
  return 'Voice error — tap voice mode again and retry.';
}

/**
 * Classify a thrown capture/transcribe failure into a VOICE_ERROR_COPY code.
 * Pure so the mapping is unit-testable; the hook only calls it and stores the code.
 *
 * @param {any} err — the thrown error (axios errors carry .response.status)
 * @param {'capture'|'transcribe'} phase
 * @returns {string}
 */
export function voiceErrorCodeFor(err, phase = 'transcribe') {
  if (phase === 'capture') {
    if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') return 'mic_denied';
    if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') return 'mic_missing';
    return 'mic_failed';
  }
  const status = err?.response?.status ?? err?.status ?? null;
  if (status === 424) return 'stt_not_configured';
  if (status === 429) return 'rate_limited';
  if (status === 401 || status === 403) return 'voice_denied';
  const raw = String(err?.response?.data?.error || err?.message || '').toLowerCase();
  if (raw.includes('not configured')) return 'stt_not_configured';
  if (raw.includes('rate_limited') || raw.includes('rate limit')) return 'rate_limited';
  return 'transcribe_failed';
}

/**
 * Timing rules for one spoken utterance. Here rather than in the hook so the
 * capture's stop/speech decisions are unit-testable without a MediaRecorder.
 */
export const VOICE_METER = {
  SILENCE_THRESHOLD: 3,
  SILENCE_DURATION_MS: 2000,
  MIN_UTTERANCE_MS: 300,
  MAX_RECORDING_MS: 30000,
};

/**
 * How loud a time-domain (waveform) frame is: average absolute deviation from the 128
 * silence baseline. Time-domain rather than frequency bins for the reason documented in
 * useCharacterSpeech.js — bin averaging under-reports anything with concentrated energy.
 *
 * @param {Uint8Array} data @returns {number}
 */
export function frameLevel(data) {
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += Math.abs(data[i] - 128);
  return data.length ? sum / data.length : 0;
}

/**
 * One metering tick's verdict for an in-flight capture.
 *
 * @param {{level: number, now: number, startedAt: number, silenceStartedAt: number|null}} s
 * @returns {{speech: boolean, silenceStartedAt: number|null, stop: boolean}}
 *   `speech` — this frame was above the noise floor (a capture that never sees one is
 *   dropped instead of being sent to STT); `stop` — close the utterance now.
 */
export function meterUtterance({ level, now, startedAt, silenceStartedAt }) {
  const elapsed = now - startedAt;
  const speech = level >= VOICE_METER.SILENCE_THRESHOLD;
  const silenceSince = speech ? null : (silenceStartedAt || now);
  const silenceStop = !speech
    && now - silenceSince >= VOICE_METER.SILENCE_DURATION_MS
    && elapsed >= VOICE_METER.MIN_UTTERANCE_MS;
  return { speech, silenceStartedAt: silenceSince, stop: silenceStop || elapsed >= VOICE_METER.MAX_RECORDING_MS };
}

/**
 * Barge-in: how loud, and for how long, the user has to be before talking over the
 * agent counts as an interruption rather than the mic hearing the agent itself.
 *
 * THRESHOLD sits well above VOICE_METER.SILENCE_THRESHOLD because this listens WHILE
 * the speakers are playing: the browser's echo canceller removes most of the agent's
 * own voice, but not all of it, and browser-engine TTS (which plays outside the page's
 * audio graph) leaks the most. SUSTAIN_MS then rejects the transients AEC misses — a
 * door, a keyboard, a cough — so a single pop never cuts the agent off mid-sentence.
 */
export const BARGE_IN = {
  THRESHOLD: 12,
  SUSTAIN_MS: 220,
  // The onset of a reply is its loudest, least-cancelled moment; ignoring it stops the
  // agent from interrupting itself on its own first syllable.
  GRACE_MS: 500,
};

/**
 * Has the user started talking over the agent?
 *
 * @param {{level: number, now: number, loudSince: number|null, speechStartedAt?: number|null}} s
 *   `loudSince` — when the current run of loud frames began, or null between runs.
 *   `speechStartedAt` — when this reply began playing; the first GRACE_MS are ignored.
 * @returns {{loudSince: number|null, bargeIn: boolean}}
 */
export function detectBargeIn({ level, now, loudSince, speechStartedAt = null }) {
  if (speechStartedAt != null && now - speechStartedAt < BARGE_IN.GRACE_MS) {
    return { loudSince: null, bargeIn: false };
  }
  if (level < BARGE_IN.THRESHOLD) return { loudSince: null, bargeIn: false };
  const since = loudSince || now;
  return { loudSince: since, bargeIn: now - since >= BARGE_IN.SUSTAIN_MS };
}

/**
 * Pure state-machine logic for the voice-mode continuous loop.
 * Decides whether the microphone should be listening, based on the
 * current voice-mode + chat state. Extracted so it can be unit-tested
 * without React hooks or DOM.
 *
 * @param {object} s
 * @param {boolean}  s.voiceMode      — voice call mode active
 * @param {boolean}  [s.micReady]     — the call's mic stream is open and metering
 * @param {boolean}  s.capturing      — MediaRecorder currently recording
 * @param {boolean}  s.arming         — getUserMedia in flight (permission prompt pending)
 * @param {boolean}  s.transcribing   — STT (or correction) in flight
 * @param {boolean}  s.sending        — a chat turn is queued/running
 * @param {boolean}  s.speaking       — TTS is playing
 * @param {string|null} s.pendingStatus — pendingJob.status or null
 * @param {string|null} [s.error]     — last voice error code, if any
 * @returns {boolean} true when the mic should start listening
 */
export function shouldListen({
  voiceMode,
  micReady = true,
  capturing,
  arming = false,
  transcribing,
  sending,
  speaking,
  pendingStatus,
  error = null,
}) {
  if (!voiceMode) return false;
  // The mic is opened once per call, on the entering click; there is nothing to
  // record from until that resolves.
  if (!micReady) return false;
  // Already listening, starting, or busy with STT/correction — nothing to do.
  if (capturing || arming || transcribing) return false;
  // A surfaced error stops the auto-retry loop: re-arming every 400ms would
  // hammer a denied mic or a rate-limited endpoint forever with no feedback.
  // The user re-enters voice mode (which clears the error) to retry.
  if (error) return false;
  // Never listen while the agent is actively thinking or playing TTS —
  // echo guard and avoid capturing the agent's own speech.
  if (sending || speaking) return false;
  // waiting_for_client = page tools running; don't interrupt
  if (pendingStatus === 'waiting_for_client') return false;
  // idle, or waiting_for_user (question pending — capture the answer)
  return true;
}

/**
 * Build the human-readable state label shown in VoiceDock.
 * @param {object} s — same shape as shouldListen, plus `lastUtterance` + `error`
 * @param {string|null} s.lastUtterance — last transcript (shown briefly)
 * @param {string|null} [s.error] — last voice error code (useVoiceMode)
 * @returns {{ state: string, label: string }}
 */
export function dockStateFor({
  voiceMode,
  capturing,
  arming = false,
  transcribing,
  correcting,
  sending,
  speaking,
  pendingStatus,
  lastUtterance,
  error = null,
}) {
  if (!voiceMode) return { state: 'idle', label: '' };
  // Active work wins over a stale error — startCapture clears it on retry.
  if (correcting) return { state: 'correcting', label: 'Polishing your message…' };
  if (transcribing) return { state: 'transcribing', label: 'Transcribing…' };
  // getUserMedia in flight: without this the dock read "Voice mode on — speak
  // anytime" while a permission prompt was pending — indistinguishable from
  // a dead mic, because no error has been thrown yet.
  if (arming && !capturing) return { state: 'arming', label: 'Starting microphone…' };
  if (capturing) {
    if (pendingStatus === 'waiting_for_user') {
      return { state: 'listening-answer', label: 'Say option one, two, or your answer…' };
    }
    return { state: 'listening', label: 'Listening…' };
  }
  // Idle with a failure to explain — the mic stopped; say why instead of
  // silently showing "Listening…"/"ready" forever.
  if (error) return { state: 'error', label: voiceErrorMessage(error) };
  if (pendingStatus === 'waiting_for_user') {
    return { state: 'question-pending', label: 'Say option one, two, or your answer…' };
  }
  if (sending) return { state: 'thinking', label: 'Thinking…' };
  if (speaking) return { state: 'speaking', label: 'Speaking… (talk over me to interrupt)' };
  if (lastUtterance) return { state: 'sent', label: lastUtterance };
  return { state: 'ready', label: 'Voice mode on — speak anytime' };
}

export default shouldListen;
